import { Select, TextInput } from '@octano/global-ui';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, Row } from 'reactstrap';
import SectionTitle from '../../../../../../components/Text/SectionTitle';
import { TranslationsKeys } from '../../../../../../locales/translations';
import { calculateAge, formatDate } from '../../../../../../utils/dates';
import {
  childrensOptions,
  drivingLicenseOptions,
  maritalStatusOptions,
  sexCIOptions,
} from '../../../../../../utils/selectOptions';
import StudentPhoto from './StudentPhoto';

const CHILE_LOWER_CASE = 'chile';

type StudentBackgroundNoSuaPDIProps = {
  prefix: string;
  photo: string | null;
  fieldNames?: {
    identificationType: string;
    identification: string;
    passportCountry: string;
    namesStudent: string;
    paternalSurname: string;
    maternalSurname: string;
    graduatedYear: string;
    age: string;
    birthday: string;
    nationality: string;
    bordCountry: string;
    bordRegion: string;
    bordCommune: string;
    bordCity: string;
    bordCustomCity: string;
    bordLocation: string;
    sexID: string;
    maritalStatus: string;
    hasChildrens: string;
    hasDrivingLicense: string;
    personalEmail: string;
    officialEmail: string;
  };
  hasEmail?: boolean;
};

/**
 * Sección de Antecedentes del estudiante SIN SUA
 */

const StudentBackgroundNoSuaPDI = ({
  prefix,
  photo,
  fieldNames = {
    identificationType: 'identificationType',
    identification: 'identification',
    passportCountry: 'passportCountry',
    nationality: 'nationality',
    namesStudent: 'namesStudent',
    paternalSurname: 'paternalSurname',
    maternalSurname: 'maternalSurname',
    graduatedYear: 'graduatedYear',
    age: 'age',
    birthday: 'birthday',
    bordCountry: 'bordCountry',
    bordRegion: 'bordRegion',
    bordCommune: 'bordCommune',
    bordCity: 'bordCity',
    bordCustomCity: 'bordCustomCity',
    bordLocation: 'bordLocation',
    sexID: 'sexID',
    maritalStatus: 'maritalStatus',
    hasChildrens: 'hasChildrens',
    hasDrivingLicense: 'hasDrivingLicense',
    personalEmail: 'personalEmail',
    officialEmail: 'officialEmail',
  },
}: StudentBackgroundNoSuaPDIProps) => {
  const { control, watch } = useFormContext();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_FILE_PERSONAL_INFORMATION,
  );

  const [birthday, identificationTypeId] = watch([
    'birthday',
    'identificationTypeId',
  ]);

  const handleCalculate = useMemo(() => {
    if (birthday) {
      return calculateAge(birthday);
    }
    return null;
  }, [birthday]);

  // Handle Regions
  const [selectedCountry] = watch([fieldNames.bordCountry]);

  const isChile = useMemo(() => {
    return String(selectedCountry?.label)
      .toLowerCase()
      .includes(CHILE_LOWER_CASE);
  }, [selectedCountry?.label]);

  return (
    <>
      <Row>
        <Col xs={12}>
          <SectionTitle text={t(`${prefix}.title`)} />
        </Col>
      </Row>
      <Row>
        <Col className="pb-5" xs={12} lg="auto">
          <FormGroup>
            <div className="m-auto mw-100" style={{ width: '121px' }}>
              <StudentPhoto defaultPhoto={photo} />
            </div>
          </FormGroup>
        </Col>
        <Col xs={12} lg={true}>
          <Row>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.identificationType}`}
                label={t(`${prefix}.${fieldNames.identificationType}`)}
                control={control}
                disabled
                shouldUnregister={true}
              />
            </Col>
            {identificationTypeId === 'PASSPORT' && (
              <Col xs={5} className="pr-1 pr-md-2">
                <Select
                  name={`${fieldNames.passportCountry}`}
                  label={t(`${prefix}.${fieldNames.passportCountry}`)}
                  options={[]}
                  isClearable={false}
                  control={control}
                  shouldUnregister={true}
                  disabled
                />
              </Col>
            )}
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.identification}`}
                label={t(`${prefix}.${fieldNames.identification}`)}
                control={control}
                disabled
                shouldUnregister={true}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.namesStudent}`}
                label={t(`${prefix}.${fieldNames.namesStudent}`)}
                control={control}
                disabled
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.paternalSurname}`}
                label={t(`${prefix}.${fieldNames.paternalSurname}`)}
                control={control}
                disabled
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.maternalSurname}`}
                label={t(`${prefix}.${fieldNames.maternalSurname}`)}
                control={control}
                disabled
                readOnly
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={`${fieldNames.birthday}`}
            label={t(`${prefix}.${fieldNames.birthday}`)}
            value={birthday ? formatDate(birthday, 'DD-MM-YYYY') : ''}
            disabled
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={`${fieldNames.age}`}
            label={t(`${prefix}.${fieldNames.age}`)}
            value={handleCalculate}
            disabled
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.nationality}`}
            label={t(`${prefix}.${fieldNames.nationality}`)}
            control={control}
            options={[]}
            isSearchable={true}
            disabled
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={fieldNames.bordCountry}
            label={t(`${prefix}.${fieldNames.bordCountry}`)}
            options={[]}
            control={control}
            disabled
          />
        </Col>
        {isChile ? (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.bordRegion}
                label={t(`${prefix}.${fieldNames.bordRegion}`)}
                options={[]}
                control={control}
                disabled
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.bordCity}
                label={t(`${prefix}.${fieldNames.bordCity}`)}
                options={[]}
                control={control}
                shouldUnregister
                disabled
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.bordCommune}
                label={t(`${prefix}.${fieldNames.bordCommune}`)}
                options={[]}
                control={control}
                shouldUnregister
                disabled
              />
            </Col>
          </>
        ) : (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={fieldNames.bordLocation}
                label={t(`${prefix}.${fieldNames.bordLocation}`)}
                control={control}
                shouldUnregister
                disabled
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={fieldNames.bordCustomCity}
                label={t(`${prefix}.${fieldNames.bordCity}`)}
                control={control}
                shouldUnregister
                disabled
              />
            </Col>
          </>
        )}
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.sexID}`}
            label={t(`${prefix}.${fieldNames.sexID}`)}
            options={sexCIOptions}
            isSearchable={false}
            control={control}
            disabled
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.maritalStatus}`}
            label={t(`${prefix}.${fieldNames.maritalStatus}`)}
            options={maritalStatusOptions}
            isSearchable={false}
            control={control}
            disabled
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.hasChildrens}`}
            label={t(`${prefix}.${fieldNames.hasChildrens}`)}
            options={childrensOptions}
            isSearchable={false}
            control={control}
            disabled
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.hasDrivingLicense}`}
            label={t(`${prefix}.${fieldNames.hasDrivingLicense}`)}
            options={drivingLicenseOptions}
            isSearchable={false}
            control={control}
            disabled
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={`${fieldNames.personalEmail}`}
            label={t(`${prefix}.${fieldNames.personalEmail}`)}
            control={control}
            readOnly
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={`${fieldNames.officialEmail}`}
            label={t(`${prefix}.${fieldNames.officialEmail}`)}
            control={control}
            disabled
            tooltip={t(`${prefix}.tooltip.email`)}
            readOnly
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={`${fieldNames.graduatedYear}`}
            label={t(`${prefix}.${fieldNames.graduatedYear}`)}
            control={control}
            shouldUnregister={true}
            disabled
          />
        </Col>
      </Row>
    </>
  );
};

export default StudentBackgroundNoSuaPDI;
